import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/ui/cn";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-sm text-sm font-semibold transition-all cursor-default disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive !no-underline",
  {
    variants: {
      variant: {
        primary:
          "border border-primary bg-primary text-primary-foreground shadow-xs hover:brightness-110 hover:text-primary-foreground active:brightness-90 active:text-primary-foreground dark:border-primary disabled:bg-muted disabled:text-muted-foreground disabled:border-transparent disabled:shadow-none disabled:opacity-100",
        default:
          "border border-border bg-background text-foreground shadow-xs hover:bg-muted active:bg-muted/80 dark:bg-secondary dark:hover:bg-secondary/60 dark:active:bg-secondary/40",
        destructive:
          "bg-destructive text-white hover:bg-destructive/90 active:bg-destructive/80 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60 disabled:bg-muted disabled:text-muted-foreground disabled:shadow-none disabled:opacity-100",
        outline:
          "border border-border bg-background shadow-xs hover:bg-muted hover:text-accent-foreground active:bg-muted/80 dark:bg-input/15 dark:hover:bg-input/30 dark:active:bg-input/25",
        secondary:
          "border border-border bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80 active:bg-secondary/70 font-medium disabled:bg-muted disabled:text-muted-foreground disabled:shadow-none disabled:opacity-100",
        ghost:
          "hover:bg-secondary hover:text-secondary-foreground active:bg-secondary/80 dark:hover:bg-secondary/70 dark:active:bg-secondary/60",
        bare: "",
        link: "text-primary-text underline-offset-4 hover:text-primary-text/80 active:opacity-70",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        xs: "h-6 rounded-sm gap-1 px-2 text-xs",
        sm: "h-8 rounded-sm gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9",
        "icon-sm": "size-8",
        "icon-lg": "size-10",
        "icon-xl": "size-12",
        "icon-2xl": "size-14",
      },
    },
    defaultVariants: {
      variant: "outline",
      size: "default",
    },
  }
);

function Button({
  className,
  variant = "outline",
  size = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
